/**
 * Pomodoro Background — fills entire viewport.
 * Supports: solid color, image, GIF, video (with audio).
 */

import React, { useRef, useEffect } from 'react'
import type { PomodoroMode } from '@turnifly/shared/stores'

interface Props {
  type: 'color' | 'image' | 'gif' | 'video'
  value: string
  mode: PomodoroMode
}

const MODE_COLORS: Record<PomodoroMode, string> = {
  work: '#BA4949',
  short_break: '#38858A',
  long_break: '#397097',
}

export const PomodoroBackground: React.FC<Props> = ({ type, value, mode }) => {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (type === 'video' && videoRef.current) {
      videoRef.current.play().catch(() => { /* autoplay blocked */ })
    }
  }, [type, value])

  if (type === 'color' || !value) {
    return (
      <div
        className="absolute inset-0 transition-colors duration-700 ease-in-out"
        style={{ backgroundColor: value || MODE_COLORS[mode] }}
      />
    )
  }

  if (type === 'image' || type === 'gif') {
    return (
      <div className="absolute inset-0">
        <img
          src={value}
          alt=""
          className="w-full h-full object-cover"
          loading="eager"
          draggable={false}
        />
        {/* Gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/50" />
      </div>
    )
  }

  if (type === 'video') {
    return (
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          src={value}
          muted
          loop
          playsInline
          preload="none"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/10 to-black/40" />
      </div>
    )
  }

  return <div className="absolute inset-0" style={{ backgroundColor: MODE_COLORS[mode] }} />
}
